// 時間前注文 (`IntradayOrderRow`) → 約定結果集計の入力 (`ExecutionSummaryRecord`) 変換。
//
// 集計・表示そのものは spot / intraday 共通の
// `~/components/bidding/common/executionSummary` (AC-BIN-022b =「スポットと同一列構成の
// 共通コンポーネント」) が持ち、 ここは「時間前固有の 母集合・単位・ステータス写像」 だけを担う。
//
// **約定率の分母の SoT は spec AC-BIN-021** (`docs/specs/bidding/intraday-bidding.md`。
// #7297 の判断 (2026-09-14 決定 A) を spec PR #7917 で反映済み)。 AC-BIN-022b は
// 「入札MW・約定MW・約定率 (%) の母集合・除外条件は AC-BIN-021 の約定率算出式と同一」
// と定めるので、 本 module の写像もこの 3 点に従う:
//   - 部分約定は終わり方 (GC 到達・板取消) によらず入札量全体を分母に入れ、
//     約定ゼロの取消は分母・分子から除外する
//   - 未約定 (板到達済み、 GC 通過時に板上で約定ゼロ) は分母に残し、
//     失効 (板未到達: 送信失敗のまま GC 到達) は除外する
//   - JEPX に一度も送らないまま失効にした札 (GC で失効にした下書き・承認待ち・差戻し
//     〈AC-BIN-099〉) は入札件数にも数えない
//   - 送信軸が SENT (JEPX受理) に到達しておらず約定 0 の札 (送信中・送信失敗を含む) も、
//     市場への到達が確認できないため分母・分子から除外する
//
// mock-app (`JepxIntradayResult.razor.cs` の `EvaluatedBidMw` = `WithdrawAt.HasValue ? Σ ExecutedMw : Mw`) は
// 部分取消を約定確定分だけにするが、 spec が 2026-10-07 の決定 C (Discussion「自動取消で終わった札の
// ステータスと約定率」) で「部分約定は終わり方によらず入札量全体を分母に入れる」と定めたので
// spec に従う。 ほかの 2 点の根拠:
//   - `mock-app/docs/domain-knowledge.md`「約定率の分母は『市場で評価された入札量』」 — 未約定を
//     分母から外すと約定率が常に 100% になり機会損失が指標から消える
//   - `mock-app/SupplyDemandBalancing/Components/Pages/JepxIntradayResult.razor.cs` の
//     `BuildResultAreaSummary` / `GetResultBids` (GC 済みコマ絞り)
//   - 移植版 `mock-app/react-app/src/models/bidding/intradayExecution.ts` の `evaluatedBidMw`
//
// TODO(business-logic): 母集合の判定 (提出済ステータス・GC 通過) と市場への到達の推定は
// 本来 backend / BFF が持つべき業務判定。 backend には `valueobject.ReachedJEPX` があるが定義が異なり
// (承認軸 + 送信軸で判定)、 本 module の推定は `expireIntradayOrdersPastGc` の実装詳細にも
// 依存している。 時間前約定結果の集計 RPC が無いため Phase 1 は client 側で判定する。
// BFF read 集約 (#3851 の bff 項目) 実装時に、 本 module は母集合判定ごと撤去して
// 「サーバー値の表示」 へ置き換える。
import {
  BiddingBidSendStatus,
  BiddingIntradayOrderStatus,
} from '@ebalance/proto/bffuser/v1/bidding_common_model_pb'

import type { ExecutionSummaryRecord } from '~/components/bidding/common/executionSummary'
import type { IntradayOrderRow } from '~/hooks/bidding/useListBiddingIntradayOrders'
import { now as serverNow } from '~/models/clock'
import { decimalToNumber, decimalToNumberOr } from '~/models/decimal'
import { isKnownOnBoardByBidNo } from '~/models/intradayJepxBidNo'
import { isExecuted } from '~/pages/IntradayBidding/models/executionResult'
import { isWithdrawnOrder } from '~/pages/IntradayBidding/models/intradayBidRow'
import { intradayGatePhase } from '~/pages/IntradayBidding/models/intradayGateClose'

/**
 * 時間前の集計入力。 KPI の約定済件数 (AC-BIN-021 の N) を約定明細と同じ判定 ({@link isExecuted})
 * で数えるため、 札ごとの約定済を持つ。
 */
export type IntradayExecutionSummaryRecord = ExecutionSummaryRecord & {
  executed: boolean
}

/**
 * JEPX へ提出された (= 板に向かった) 注文の約定軸ステータス。
 *
 * 時間前の約定軸は Submit で PLACED になり、 そこから 部分約定 / 全量約定 /
 * 取消 / 送信失敗 (FAILED) / GC 終端の 未約定 (UNMATCHED) / 失効 (EXPIRED) へ進む
 * (backend `IntradayOrder.Submit` / `Withdraw` / `WithMatchResult` /
 * `ResolveAtGateClose`)。 UNMATCHED / EXPIRED は GC スイープが約定ゼロの札へ書く値で、
 * 変更前は FAILED として本集合に入っていた。 外すと GC 後の札が入札件数・分母から
 * 消え、 約定率が実際より高く出る (AC-BIN-021 / AC-BIN-022b)。 分母から外すかは
 * 板への到達で決まる ({@link isExcludedFromFillRate})。
 * ここに無い値 (UNSPECIFIED / DRAFT / PENDING / APPROVED) は Submit 前の札。
 * EXPIRED のうち一度も送っていない札は {@link isExpiredWithoutEverSent} で外す。
 */
const SUBMITTED_BID_STATUSES: ReadonlySet<BiddingIntradayOrderStatus> = new Set(
  [
    BiddingIntradayOrderStatus.PLACED,
    BiddingIntradayOrderStatus.PARTIAL_MATCHED,
    BiddingIntradayOrderStatus.ALL_MATCHED,
    BiddingIntradayOrderStatus.CANCELLED,
    // 自動取消は Slice 64 (#11312) までは取消と同じ側 (分母から外れる側) に置く。
    BiddingIntradayOrderStatus.AUTO_CANCELLED,
    BiddingIntradayOrderStatus.FAILED,
    BiddingIntradayOrderStatus.UNMATCHED,
    BiddingIntradayOrderStatus.EXPIRED,
  ],
)

/**
 * GC を通過したコマの札か (= 約定結果として確定しているか)。
 *
 * mock (`JepxIntradayResult.GetResultBids`) は明細も集計も GC 済みコマに絞る。
 * まだ約定機会が残っているコマを混ぜると、 約定率が「これから約定する量」 のぶん
 * 不当に下がる (移植版 `intradayExecution.ts` の `isSettled` gate と同じ理由)。
 *
 * **GC 絞りは spec 本文に未記載で、 #7298 の EN 暫定判断 (2026-09-19) に従う**
 * (集計・KPI は GC 済みコマのみ、 明細は絞らない。 PdM の事後確認は #8404、
 * spec への明記は #8411 で追跡)。
 *
 * 境界 (`now === gcAt`) は閉鎖済み扱い。 backend の編集・板取消ゲート
 * (`!now.Before(existing.GcAt())`) と同じ側に揃える。
 *
 * `gcAt` 未設定は **判定不能なので母集合に残す**。 gc_at は proto 上 optional では
 * ないが (`bidding_intraday_model.proto`)、 未設定で届いた札を落とすと
 * 「約定しているのに集計に出ない」 という気づきにくい欠落になるため、
 * 数字が出る側に倒す。
 */
const isGateClosed = (row: IntradayOrderRow, now: Date): boolean =>
  intradayGatePhase(row.gcAt, now) !== 'open'

/**
 * 送信軸が JEPX にまだ送っていない側の値 (送信軸なし・送信待ち・予約中)。
 * 送信中以降は一度は送っている (backend `BidSendPhaseNotSent` が 予約中 / 送信待ち)。
 */
const NOT_YET_SENT_STATUSES: ReadonlySet<BiddingBidSendStatus> = new Set([
  BiddingBidSendStatus.UNSPECIFIED,
  BiddingBidSendStatus.ENQUEUED,
  BiddingBidSendStatus.RESERVED,
])

/**
 * JEPX に一度も送らないまま失効にした札か。 AC-BIN-021 は入札件数に数えないと定める。
 * GC で失効にした下書き・承認待ち・差戻しの札 (AC-BIN-099。 承認ステータスは N/A、 送信軸なし) と、
 * 予約中・送信待ちのまま失効にした札が当たる。
 *
 * 送ったが届かなかったと確かめて失効にした札 (送信失敗確定) は一度送っているので当たらない。
 * JEPX 入札番号か約定量を持つ札は板に出たことがあるので、 送信軸が送信前に見えても
 * 当たらない (数字を落とさない側に倒す)。
 */
const isExpiredWithoutEverSent = (row: IntradayOrderRow): boolean =>
  row.bidStatus === BiddingIntradayOrderStatus.EXPIRED &&
  NOT_YET_SENT_STATUSES.has(row.sendStatus) &&
  !isKnownOnBoardByBidNo(row) &&
  decimalToNumberOr(row.matchedVolumeMwh) <= 0

/**
 * エリア × 売買 別 集計 (AC-BIN-022b) の母集合に入る札か。
 *
 * 外すのは 3 種類だけ:
 *   1. 未提出 (約定軸が Submit 前) — 下書き / 承認申請中 / 承認済 / 差戻し は
 *      Submit まで約定軸が DRAFT のまま (`IntradayOrder.RequestApproval`)。
 *      JEPX に出していない札として「入札件数」 にも数えない (= 行も出さない)
 *   2. JEPX に一度も送らないまま失効にした札 — {@link isExpiredWithoutEverSent}
 *   3. GC 未通過コマの札 — 上記 {@link isGateClosed}
 */
const isInResultPopulation = (row: IntradayOrderRow, now: Date): boolean =>
  SUBMITTED_BID_STATUSES.has(row.bidStatus) &&
  !isExpiredWithoutEverSent(row) &&
  isGateClosed(row, now)

/**
 * 市場で評価された入札量 (MW) = 約定率の分母。
 *
 * AC-BIN-021: 部分約定は終わり方 (GC 到達・板取消) によらず入札量全体を分母に入れ、
 * 約定ゼロの取消 (板取消・予約取消) は分母から除く。 約定量が残る取消札を約定量だけにすると、
 * 4 / 10 MW 約定して取り消した札が 100% と出て機会損失が消える。
 *
 * 取消の判定は [[isWithdrawnOrder]] (約定軸 CANCELLED または承認軸 CANCELLED) で行う。
 * BFF の `BiddingIntradayOrderListItem` は `withdrawn_at` を持たないため使えない。
 * backend `IntradayOrder.Withdraw` が `withdrawnAt` と両軸の CANCELLED を同一
 * トランザクションで立てる (= ラベル文言ではなく状態そのもの) ので等価。
 *
 * 要確認の札 (AC-BIN-127) は取り込み元が返した約定の分だけを分母に入れ、 残数量は入れない
 * (AC-BIN-021)。 母集合は GC 済みのコマに限るので、 ここに来る要確認の札は GC を迎えた札。
 */
const evaluatedBidVolumeMw = (
  row: IntradayOrderRow,
  execVolumeMw: number,
): number => {
  if (row.needsReview) return execVolumeMw
  return isWithdrawnOrder(row) && execVolumeMw <= 0
    ? 0
    : decimalToNumberOr(row.volumeMwh)
}

/**
 * 約定率の分母・分子から外す札か。 入札件数には数える (= 除外は数量側だけ)。
 *
 * AC-BIN-021 の「送信軸が SENT〈JEPX受理〉に到達しておらず約定 0 の札〈送信中・送信失敗を
 * 含む〉も、 市場への到達が確認できないため分母・分子から除外する」 の実装
 * (#7297 決定 A / spec PR #7917)。
 *
 * 分母に残すのは **市場に到達したことが確認できる札**だけ。 domain-knowledge の
 * 「失効 = **JEPX に札自体が届いていない**ため約定対象にすらならない」 に当たる札は、
 * 市場で評価された量が存在しないため分母にも分子にも入れない。
 *
 * 到達の判定は次のとおり:
 *   - 約定軸 `UNMATCHED` (未約定) — backend の GC 判定 (`IntradayOrder.ResolveAtGateClose`)
 *     は板に届いた札にしか未約定を書かない。 送信軸が SENT の札に加え、 送信失敗のまま
 *     GC を迎え、 JEPX の受理済み入札で板に並んでいたと確かめた札 (送信軸は
 *     SEND_FAILED_FINAL 等のまま) も未約定になる (AC-BIN-115)
 *   - 送信軸 `SENT` (= eSLJ / JEPX が入札を受理した状態。 `BidSendStatus` の godoc)
 *   - 約定量 > 0 — 約定は市場でしか起きないので、 送信軸の更新が約定同期に遅れていても
 *     到達済みと判る (数字を落とさない側に倒す)
 *   - JEPX 入札番号あり ({@link isKnownOnBoardByBidNo}) — 取り込みが板で見つけて入札番号を
 *     紐付けた札は、 送信軸が送信失敗のままでも板に並んでいる (AC-BIN-115、 #10491)
 *
 * GC 前の入札中 (PLACED) や、 届いたかを確かめている最中の送信失敗 (FAILED) は約定軸に
 * 到達の有無が出ない。 再送中 (SEND_FAILED) / 予約中 (RESERVED) / 送信待ち (ENQUEUED) /
 * 送信中 (SENDING) の札や確かめている最中の送信失敗を分母に残すと入札 MW が水増しされ、
 * 約定率が実績より低く出る。
 *
 * 板に並んで約定ゼロのまま GC 到達した札 (AC-BIN-035/036 の「未約定」) は **分母に残す**。
 * これを外すと約定率が常に 100% になり機会損失が指標から消える (domain-knowledge)。
 * 板に届かなかった・届いたと確かめられなかった失効 (EXPIRED) は外れる (AC-BIN-021)。
 */
const isExcludedFromFillRate = (
  row: IntradayOrderRow,
  execVolumeMw: number,
): boolean =>
  row.bidStatus !== BiddingIntradayOrderStatus.UNMATCHED &&
  row.sendStatus !== BiddingBidSendStatus.SENT &&
  !isKnownOnBoardByBidNo(row) &&
  execVolumeMw <= 0

/**
 * 表示中の注文一覧を約定結果集計の入力へ変換する。
 *
 * 数量は換算しない。 コマ別数量の保持・書込 contract は **MW** で統一されており
 * (issue #4285。 `volumeMwh` / `matchedVolumeMwh` の `mwh` は旧 spec 時代の名残で
 * 格納値は MW。 SoT: `~/models/komaQuantity` の module docstring)、 AC-BIN-022b の
 * 「入札MW」「約定MW」 も MW 建てなので、 ここに MW↔MWh の境界変換は存在しない。
 *
 * @param now GC 判定に使う現在時刻。 呼び出し側は 1 分ごとに更新される
 *   `useNowByMinute()` を渡す。
 */
export const toExecutionSummaryRecords = (
  rows: IntradayOrderRow[],
  now: Date = serverNow(),
): IntradayExecutionSummaryRecord[] =>
  rows
    .filter((row) => isInResultPopulation(row, now))
    .map((row) => {
      // 未約定は約定量 0 MW / 約定価格なし (= AC-BIN-022b の「未約定 (約定量 0) の行は
      // 加重平均約定価格を『—』」 を集計側が判定できる形)。
      const execVolumeMw = decimalToNumberOr(row.matchedVolumeMwh)
      return {
        areaCode: row.areaCode,
        // 売買方向は写すだけで既定値に倒さない。 UNSPECIFIED を売り扱いにすると
        // データ異常が「売」 の行に紛れて画面から消える (エリア×売買集計では
        // `TradeSideLabel` の fallback 表示で可視化される)。
        side: row.direction,
        bidVolumeMw: evaluatedBidVolumeMw(row, execVolumeMw),
        execVolumeMw,
        execPriceYenPerKwh: decimalToNumber(row.matchedPriceYenPerKwh),
        excluded: isExcludedFromFillRate(row, execVolumeMw),
        executed: isExecuted(row),
      }
    })
